import { useState, useEffect } from "react";
import { Button, DenkerLogo, Icons } from "@/components/ui";
import { CanvasBackground } from "./canvas-hero";

interface AuthPageLayoutProps {
  children: React.ReactNode;
}

type Theme = "dark" | "light";

export function AuthPageLayout({ children }: AuthPageLayoutProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    const stored = localStorage.getItem("theme");
    return stored === "light" ? "light" : "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark", "light");
    root.classList.add(theme);
  }, [theme]);

  function toggleTheme() {
    const next: Theme = theme === "dark" ? "light" : "dark";
    localStorage.setItem("theme", next);
    setTheme(next);
  }

  return (
    <div
      className="liquid-glass-enabled relative min-h-screen overflow-hidden bg-canvas"
      data-testid="auth-layout"
      style={{
        backgroundImage: "radial-gradient(circle, var(--color-canvas-dot) 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    >
      {/*
        SVG liquid-glass distortion filter — must live in the DOM for
        `backdrop-filter: url(#liquid-glass-distortion)` in index.css to work.
      */}
      <svg style={{ display: "none" }} aria-hidden="true">
        <defs>
          <filter
            id="liquid-glass-distortion"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018 0.014"
              numOctaves="3"
              seed="5"
              result="noise"
            />
            <feColorMatrix type="saturate" values="0" in="noise" result="grayNoise" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="grayNoise"
              scale="2"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      {/* Page gradient overlay */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{ background: "var(--gradient-page-overlay)" }}
      />

      {/* Theme toggle — top-right corner */}
      <div className="absolute right-4 top-4 z-20">
        <Button
          variant="secondary"
          size="icon"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          data-testid="auth-theme-toggle"
        >
          {theme === "dark" ? (
            <Icons.Sun className="h-4 w-4" />
          ) : (
            <Icons.Moon className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Full-canvas animated scene */}
      <CanvasBackground />

      {/* Mobile readability scrim: keep the scene present without competing with the form. */}
      <div
        className="pointer-events-none absolute inset-0 z-[1] bg-canvas/70 backdrop-blur-[72px] md:hidden"
        aria-hidden="true"
      />

      {/* Centered glass form card */}
      <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
        <div
          className="relative w-full max-w-[420px] rounded-2xl border border-glass-stroke-light bg-glass-fill-heavy shadow-card-modal backdrop-blur-glass sm:bg-glass-fill"
          style={{ padding: "40px" }}
          data-testid="auth-card"
        >
          {/* Specular top-edge highlight */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-2xl"
            style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.60) 50%, transparent 100%)" }}
            aria-hidden="true"
          />

          {/* Official Denker wordmark */}
          <div className="relative z-10 mb-7" data-testid="auth-logo">
            <DenkerLogo
              variant="wordmark"
              color="auto"
              height={28}
            />
          </div>

          <div className="relative z-10">{children}</div>
        </div>
      </div>
    </div>
  );
}
